import {SerializedStyles} from '@emotion/react';
import {css, Typography, useTheme} from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import * as React from 'react';
import {useIntl} from 'react-intl';
import {getIconComponentByName} from 'src/js/common/icons/icon_helpers';
import {GetStylesFunction} from 'src/js/common/types';
import TranslateUtils from 'src/js/common/utils/translate_utils';

interface OwnProps {
  children?: React.ReactNode;
  label?: string;
  iconName?: string;
  useFixedViewBox?: boolean | number | string;
  iconCss?: SerializedStyles;
  labelCss?: SerializedStyles;
  betweenItems?: JSX.Element;
  style?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  childrenWrapperStyle?: React.CSSProperties;
}

type Props = OwnProps;

export const IconHeaderWithSearch: React.FC<Props> = (props) => {
  const {children, label, iconName, useFixedViewBox, iconCss, labelCss, betweenItems, style, labelStyle, childrenWrapperStyle} = props;
  const theme = useTheme();
  const intl = useIntl();
  const styles = getStyles({theme, params: {iconCss, labelCss}});
  let RenderedIconComponent;

  if (iconName) {
    RenderedIconComponent = getIconComponentByName(iconName);
  }

  const useFixedViewBoxNumber = useFixedViewBox === true ? 24 : useFixedViewBox || 24;
  let fixedViewBox: string | undefined;
  switch (useFixedViewBox?.constructor?.name) {
    case 'Number':
      fixedViewBox = `0 0 ${useFixedViewBoxNumber} ${useFixedViewBoxNumber}`;
      break;
    case 'String':
      fixedViewBox = useFixedViewBox as string;
      break;
    default:
      fixedViewBox = '0 0 24 24';
      break;
  }
  return (
    <div data-name={'icon-header-with-search--header-wrapper'} css={styles.headerWrapper} style={style}>
      <div css={styles.iconWithLabelWrapper}>
        {RenderedIconComponent ? (
          <SvgIcon
            component={RenderedIconComponent}
            inheritViewBox={!useFixedViewBox}
            viewBox={useFixedViewBox ? fixedViewBox : undefined}
            css={styles.icon}
          />
        ) : null}
        {label ? (
          <Typography data-name={'icon-header-with-search--label'} css={styles.label} variant={'cardHeader'} style={labelStyle}>
            {TranslateUtils.translate(label, intl)}
          </Typography>
        ) : null}
      </div>
      {betweenItems}
      <div css={styles.childrenWrapper} style={childrenWrapperStyle}>
        {children}
      </div>
    </div>
  );
};

IconHeaderWithSearch.displayName = 'IconHeaderWithSearch';

const getStyles: GetStylesFunction = (options) => ({
  headerWrapper: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0;
    gap: 8px;
    min-height: 32px;
    width: 100%;
    justify-content: space-between;
    flex-shrink: 0;
  `,
  icon: css(
    {
      color: 'var(--dlTheme-300)',
      width: '24px',
      height: '24px',
      marginRight: `${options?.theme?.spacing(2)}`,
    },
    options?.params?.iconCss
  ),
  label: css(
    {
      color: options?.theme?.palette.custom.headerLabel as string,
    },
    options?.params?.labelCss
  ),
  iconWithLabelWrapper: css`
    display: inline-flex;
    align-items: center;
  `,
  childrenWrapper: css`
    display: inline-flex;
    gap: 16px;
  `,
});
